/**
 * Storage service for persisting chat sessions and tabs
 * Data is scoped by account (user + company) + document and supports optional
 * backend syncing.
 */

/* global Office */

import {
  ChatMessage,
  TabsState,
  Tab,
  ChatSession,
  SlideRef,
  generateId,
  PresentationContent,
} from "../types";
import { STORAGE_KEYS } from "../config/constants";
import { createChatSessionId } from "../../shared/utils/sessionId";
import { normalizeSelectedUserFileIds } from "../../shared/researchSelection";
import { apiFetch } from "./apiClient";

const REF_DECK_KEY = "finster_reference_deck";

const STORAGE_KEY_PREFIX = STORAGE_KEYS.TABS_PREFIX;
const REVIEW_KEY_PREFIX = STORAGE_KEYS.REVIEW_RESULTS_PREFIX;
const SESSION_DOC_KEY = "finster_session_doc_id";
const OAUTH_STATE_KEY = "workos_state";
const STORAGE_VERSION = 3;
const TABS_RETENTION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const REVIEW_RETENTION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const REMOTE_SYNC_DEBOUNCE_MS = 1000;
const LOCAL_TABS_SAVE_DEBOUNCE_MS = 150;
// Cap streamingThinking size on save so a runaway reasoning trace can't
// blow past the ~5 MB localStorage origin budget. Trims from the start so
// the tail (most recent reasoning) survives — that's what users care about
// when re-opening an interrupted tab.
const MAX_PERSISTED_THINKING_BYTES = 16 * 1024;
const INTERRUPTED_LOADING_LABEL = "Interrupted";

// Cache the current document ID
let currentDocumentId: string | null = null;
// Override that, when set, takes precedence over the Office.context.document.url
// derived id. This is what lets a single task pane that follows the user
// across multiple open PowerPoint files (per
// `docs/powerpoint/identifying_presentations_from_finster_chat.md`) key chat
// history by FactSet's `openPresentationId` instead of the URL of whichever
// presentation the addin happened to load against first.
//
// `useTabs` polls the FactSet bridge and calls `setActivePresentationId(...)`
// every time the active presentation changes. Everything downstream
// (`saveTabsState`, `loadTabsState`, `presentationKey.documentId`, the
// in-flight registry, the per-tab routing in `applyToSourceTabs`) reads this
// through `getDocumentId()` and therefore picks up the new presentation
// without any extra plumbing.
let activePresentationIdOverride: string | null = null;
const ACTIVE_PRESENTATION_SESSION_KEY = "finster_active_presentation_id";
let currentUserScope = "anonymous";
let currentScopeGeneration = 0;
let backendPersistenceAvailable: boolean | null = null;

interface PersistedEnvelope<T> {
  version: number;
  storedAt: number;
  expiresAt: number;
  userScope: string;
  documentId: string;
  payload: T;
}

interface RemoteDocumentStatePayload {
  tabs?: PersistedEnvelope<TabsState>;
  review?: PersistedEnvelope<unknown[]>;
}

interface StorageScopeToken {
  userScope: string;
  generation: number;
}

interface PendingRemoteSyncEntry {
  documentId: string;
  scopeToken: StorageScopeToken;
  payload: {
    tabs?: PersistedEnvelope<TabsState>;
    review?: PersistedEnvelope<unknown[]>;
  };
}

interface PendingLocalTabsEntry {
  documentId: string;
  scopeToken: StorageScopeToken;
  state: TabsState;
}

const pendingRemoteSync = new Map<string, PendingRemoteSyncEntry>();
const pendingRemoteTimers = new Map<string, ReturnType<typeof setTimeout>>();
const pendingLocalTabsState = new Map<string, PendingLocalTabsEntry>();
const pendingLocalTabsTimers = new Map<string, ReturnType<typeof setTimeout>>();
const inFlightRemoteRequests = new Set<AbortController>();
// Session fallback for hosts where localStorage is unavailable or at quota.
// Keys remain account + document scoped, matching the persistent envelope keys.
const volatileTabsEnvelopes = new Map<string, PersistedEnvelope<TabsState>>();
let hasPrunedExpiredEntries = false;

function withLocalStorage<T>(callback: () => T, fallback: T): T {
  try {
    return callback();
  } catch {
    return fallback;
  }
}

function normalizeUserScope(userId: string | null | undefined): string {
  const normalized = String(userId || "").trim();
  if (!normalized) return "anonymous";
  // Preserve every identity byte. Replacing/truncating characters can collapse
  // distinct tenant ids into the same local key.
  return encodeURIComponent(normalized);
}

function captureStorageScope(
  accountScopeKey?: string | null
): StorageScopeToken | null {
  const requestedScope =
    accountScopeKey === undefined
      ? currentUserScope
      : normalizeUserScope(accountScopeKey);
  if (requestedScope !== currentUserScope) return null;
  return {
    userScope: requestedScope,
    generation: currentScopeGeneration,
  };
}

function isStorageScopeCurrent(scopeToken: StorageScopeToken): boolean {
  return (
    scopeToken.userScope === currentUserScope &&
    scopeToken.generation === currentScopeGeneration
  );
}

function getPendingWorkKey(
  scopeToken: StorageScopeToken,
  documentId: string
): string {
  return JSON.stringify([
    scopeToken.generation,
    scopeToken.userScope,
    documentId,
  ]);
}

function cancelPendingScopeWork(): void {
  pendingRemoteTimers.forEach((timer) => clearTimeout(timer));
  pendingRemoteTimers.clear();
  pendingRemoteSync.clear();
  pendingLocalTabsTimers.forEach((timer) => clearTimeout(timer));
  pendingLocalTabsTimers.clear();
  pendingLocalTabsState.clear();
  inFlightRemoteRequests.forEach((controller) => controller.abort());
  inFlightRemoteRequests.clear();
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isEnvelope(value: unknown): value is PersistedEnvelope<unknown> {
  if (!isObject(value)) return false;
  return (
    typeof value.version === "number" &&
    typeof value.storedAt === "number" &&
    typeof value.expiresAt === "number" &&
    typeof value.userScope === "string" &&
    typeof value.documentId === "string" &&
    Object.prototype.hasOwnProperty.call(value, "payload")
  );
}

function createEnvelope<T>(
  payload: T,
  retentionMs: number,
  documentId: string,
  userScope: string = currentUserScope
): PersistedEnvelope<T> {
  const storedAt = Date.now();
  return {
    version: STORAGE_VERSION,
    storedAt,
    expiresAt: storedAt + retentionMs,
    userScope,
    documentId,
    payload,
  };
}

function getScopedSuffix(
  documentId: string,
  userScope: string = currentUserScope
): string {
  return `${userScope}:${documentId}`;
}

function getTabsStorageKeyForDocument(
  documentId: string,
  userScope: string = currentUserScope
): string {
  return `${STORAGE_KEY_PREFIX}${getScopedSuffix(documentId, userScope)}`;
}

function getReviewStorageKeyForDocument(
  documentId: string,
  userScope: string = currentUserScope
): string {
  return `${REVIEW_KEY_PREFIX}${getScopedSuffix(documentId, userScope)}`;
}

/**
 * Get a unique identifier for the current document.
 *
 * Priority order:
 *   1. `activePresentationIdOverride` — set by `setActivePresentationId(...)`
 *      from the FactSet `openPresentationId` poller in `useTabs`. This is the
 *      canonical per-presentation identifier in a single-task-pane PowerPoint
 *      session and the only one that actually changes when the user switches
 *      between open presentations.
 *   2. `Office.context.document.url` hash — useful only as a cross-session
 *      anchor for the initial presentation the task pane loaded against;
 *      sticky to that file for the lifetime of the pane.
 *   3. Random per-session id stored in `sessionStorage` — fallback for hosts
 *      that don't expose either of the above.
 */
export function getDocumentId(): string {
  if (activePresentationIdOverride) {
    return `pres_${activePresentationIdOverride}`;
  }

  if (currentDocumentId) {
    return currentDocumentId;
  }

  try {
    const url = typeof Office !== "undefined" ? Office.context?.document?.url : undefined;
    if (url && url.length > 0) {
      // Hash the URL to create a shorter key
      currentDocumentId = hashString(url);
      console.log(`[Storage] Document ID from URL: ${currentDocumentId}`);
      return currentDocumentId;
    }
  } catch (e) {
    console.log("[Storage] Could not get document URL:", e);
  }

  // Fallback: use session storage for unsaved documents
  let sessionId = withLocalStorage(() => sessionStorage.getItem(SESSION_DOC_KEY), null as string | null);
  if (!sessionId) {
    sessionId = "doc_" + generateId();
    withLocalStorage(() => {
      sessionStorage.setItem(SESSION_DOC_KEY, sessionId as string);
      return null;
    }, null);
  }
  currentDocumentId = sessionId;
  console.log(`[Storage] Document ID from session: ${currentDocumentId}`);
  return currentDocumentId;
}

/**
 * Reset the cached document ID (call when document changes).
 * Clears BOTH the URL-derived cache and the FactSet override so the next
 * `getDocumentId()` call re-derives identity from scratch.
 */
export function resetDocumentId(): void {
  currentDocumentId = null;
  activePresentationIdOverride = null;
  withLocalStorage(() => {
    sessionStorage.removeItem(SESSION_DOC_KEY);
    sessionStorage.removeItem(ACTIVE_PRESENTATION_SESSION_KEY);
    return null;
  }, null);
}

/**
 * Override the storage layer's notion of "which presentation is currently
 * active" with FactSet's `openPresentationId` (or any caller-supplied stable
 * per-presentation id). Pass `null` to clear the override and fall back to the
 * Office URL / session id derivation.
 *
 * This is what makes per-presentation chat history work in a single
 * task-pane session: `useTabs` polls
 * `getActivePresentationRefViaIntents()` and calls this whenever the active
 * presentation changes, so every subsequent `getDocumentId()` /
 * `saveTabsState` / `loadTabsState` automatically keys against the new
 * presentation without any other code in the addin needing to know.
 *
 * We mirror the value into `sessionStorage` so a webpack HMR reload during
 * development doesn't strand the user on the old (URL-derived) key.
 */
export function setActivePresentationId(id: string | null): void {
  const normalized = id && id.trim().length > 0 ? id.trim() : null;
  if (activePresentationIdOverride === normalized) return;
  activePresentationIdOverride = normalized;
  withLocalStorage(() => {
    if (normalized) {
      sessionStorage.setItem(ACTIVE_PRESENTATION_SESSION_KEY, normalized);
    } else {
      sessionStorage.removeItem(ACTIVE_PRESENTATION_SESSION_KEY);
    }
    return null;
  }, null);
  console.log(
    `[Storage] Active presentation id ${normalized ? `set to ${normalized}` : "cleared"}`
  );
}

/**
 * Read the FactSet active-presentation override directly (sync). Returns
 * `null` if no override is in effect. Used by callers that need to know
 * whether the FactSet id is driving the storage layer (e.g. boot-time
 * hydration in `useTabs`).
 */
export function getActivePresentationId(): string | null {
  return activePresentationIdOverride;
}

// Restore any override that was active before a hot reload so the first
// `getDocumentId()` after module init returns the FactSet-derived key
// instead of the (potentially stale) Office URL key.
(function hydrateActivePresentationIdFromSession() {
  const persisted = withLocalStorage(
    () => sessionStorage.getItem(ACTIVE_PRESENTATION_SESSION_KEY),
    null as string | null
  );
  if (persisted && persisted.length > 0) {
    activePresentationIdOverride = persisted;
  }
})();

/**
 * Set account scope for local persisted keys. Scope changes cancel queued and
 * in-flight work and reset backend availability for a fresh tenant-bound probe.
 */
export function setStorageUserScope(userId: string | null | undefined): void {
  const newScope = normalizeUserScope(userId);
  if (newScope === currentUserScope) return;

  cancelPendingScopeWork();
  currentScopeGeneration += 1;
  currentUserScope = newScope;
  backendPersistenceAvailable = null;
}

/**
 * Simple string hash function.
 */
function hashString(str: string, prefix: string = "doc"): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `${prefix}_${Math.abs(hash).toString(36)}`;
}

function parseTabsState(raw: unknown, documentId: string): TabsState | null {
  if (!isObject(raw) || !Array.isArray(raw.tabs) || raw.tabs.length === 0) {
    return null;
  }

  const activeTabId = typeof raw.activeTabId === "string" ? raw.activeTabId : raw.tabs[0]?.id;
  if (!activeTabId || typeof activeTabId !== "string") {
    return null;
  }

  const migratedTabs = (raw.tabs as Tab[]).map((tab) => {
    const fallbackIndex = typeof tab.session?.slideIndex === "number" ? tab.session.slideIndex : 0;
    const fallbackSlideNumber =
      typeof tab.session?.slideNumber === "number" ? tab.session.slideNumber : fallbackIndex + 1;
    const migratedSession: ChatSession = {
      ...tab.session,
      slideId:
        typeof (tab.session as Partial<ChatSession>).slideId === "string"
          ? ((tab.session as Partial<ChatSession>).slideId as string)
          : "",
      slideIndex: fallbackIndex,
      slideNumber: fallbackSlideNumber,
      name: tab.session?.name || `Slide ${fallbackSlideNumber}`,
      // Sessions written before Your Files became chat-owned have no list, and
      // a payload from storage is untrusted input: normalize rather than hand
      // the composer ids it would send to the backend unchecked.
      selectedUserFileIds: normalizeSelectedUserFileIds(
        Array.isArray(tab.session?.selectedUserFileIds) ? tab.session.selectedUserFileIds : []
      ),
    };
    // STORAGE_VERSION 3+: loading flags survive a reload so the user sees
    // the same in-flight visual state they left. `reconcileLoadingWithRegistry`
    // (called by useTabs after load) demotes any tab that's no longer in
    // flight to an "Interrupted" state. Older payloads (version <= 2) never
    // persisted these fields, so the fallback defaults are equivalent.
    return {
      ...tab,
      session: migratedSession,
      isLoading: typeof tab.isLoading === "boolean" ? tab.isLoading : false,
      loadingStatus: typeof tab.loadingStatus === "string" ? tab.loadingStatus : "",
      streamingThinking:
        typeof tab.streamingThinking === "string" ? tab.streamingThinking : "",
      thoughtProcessCollapseSignal:
        typeof tab.thoughtProcessCollapseSignal === "number"
          ? tab.thoughtProcessCollapseSignal
          : undefined,
      visionFeedback: typeof tab.visionFeedback === "string" ? tab.visionFeedback : "",
      refinementStep:
        typeof tab.refinementStep === "number" ? tab.refinementStep : 0,
    };
  });

  if (migratedTabs.length === 0) return null;
  const activeTabExists = migratedTabs.some((tab) => tab.id === activeTabId);
  return {
    documentId,
    tabs: migratedTabs,
    activeTabId: activeTabExists ? activeTabId : migratedTabs[0].id,
  };
}

function parseReviewResults(raw: unknown): unknown[] | null {
  if (!Array.isArray(raw)) return null;
  return raw;
}

function parseJson(raw: string | null): unknown | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function removeStorageKey(key: string): void {
  withLocalStorage(() => {
    localStorage.removeItem(key);
    return null;
  }, null);
}

function readEnvelope<T>(
  key: string,
  documentId: string,
  userScope: string,
  payloadParser: (value: unknown) => T | null
): PersistedEnvelope<T> | null {
  const rawScoped = withLocalStorage(() => localStorage.getItem(key), null as string | null);
  const parsedScoped = parseJson(rawScoped);

  if (isEnvelope(parsedScoped)) {
    if (
      parsedScoped.userScope !== userScope ||
      parsedScoped.documentId !== documentId
    ) {
      // A stale writer must never make a mismatched envelope readable from
      // another account/document key.
      removeStorageKey(key);
      return null;
    }
    if (parsedScoped.expiresAt <= Date.now()) {
      removeStorageKey(key);
      return null;
    }
    const payload = payloadParser(parsedScoped.payload);
    if (!payload) {
      removeStorageKey(key);
      return null;
    }
    return { ...(parsedScoped as PersistedEnvelope<T>), payload };
  }

  // Unscoped and old user-only keys have no trustworthy company identity.
  // They are intentionally left untouched and are never auto-attributed to
  // the currently authenticated tenant.
  return null;
}

function writeEnvelope<T>(key: string, envelope: PersistedEnvelope<T>): void {
  withLocalStorage(() => {
    localStorage.setItem(key, JSON.stringify(envelope));
    return null;
  }, null);
}

function pruneExpiredByPrefix(prefix: string): void {
  withLocalStorage(() => {
    const keysToDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const parsed = parseJson(localStorage.getItem(key));
      if (isEnvelope(parsed) && parsed.expiresAt <= Date.now()) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach((key) => localStorage.removeItem(key));
    return null;
  }, null);
}

function maybePruneExpiredEntries(): void {
  if (hasPrunedExpiredEntries) return;
  hasPrunedExpiredEntries = true;
  pruneExpiredByPrefix(STORAGE_KEY_PREFIX);
  pruneExpiredByPrefix(REVIEW_KEY_PREFIX);
}

async function fetchRemoteDocumentState(
  documentId: string,
  scopeToken: StorageScopeToken
): Promise<RemoteDocumentStatePayload | null> {
  if (!isStorageScopeCurrent(scopeToken)) return null;
  if (scopeToken.userScope === "anonymous") return null;
  if (backendPersistenceAvailable === false) return null;

  const controller = new AbortController();
  inFlightRemoteRequests.add(controller);
  try {
    const response = await apiFetch(
      `/api/persistence/state/${encodeURIComponent(documentId)}`,
      { signal: controller.signal }
    );
    if (!isStorageScopeCurrent(scopeToken)) return null;
    if (response.status === 404 || response.status === 501) {
      backendPersistenceAvailable = false;
      return null;
    }
    if (response.status === 401 || response.status === 403) {
      // Optional persistence must never force an app-wide logout.
      backendPersistenceAvailable = false;
      return null;
    }
    if (!response.ok) {
      return null;
    }

    backendPersistenceAvailable = true;
    const data = (await response.json()) as { state?: RemoteDocumentStatePayload | null };
    if (!isStorageScopeCurrent(scopeToken)) return null;
    if (!isObject(data) || !isObject(data.state)) {
      return null;
    }
    return data.state;
  } catch {
    return null;
  } finally {
    inFlightRemoteRequests.delete(controller);
  }
}

async function flushRemoteSync(pendingKey: string): Promise<void> {
  const entry = pendingRemoteSync.get(pendingKey);
  if (!entry) return;

  pendingRemoteSync.delete(pendingKey);
  pendingRemoteTimers.delete(pendingKey);

  if (!isStorageScopeCurrent(entry.scopeToken)) return;
  if (backendPersistenceAvailable === false) return;

  const controller = new AbortController();
  inFlightRemoteRequests.add(controller);
  try {
    const response = await apiFetch(
      `/api/persistence/state/${encodeURIComponent(entry.documentId)}`,
      {
        method: "PUT",
        body: JSON.stringify(entry.payload),
        signal: controller.signal,
      }
    );
    if (!isStorageScopeCurrent(entry.scopeToken)) return;
    if (response.status === 404 || response.status === 501) {
      backendPersistenceAvailable = false;
      return;
    }
    if (response.status === 401 || response.status === 403) {
      backendPersistenceAvailable = false;
      return;
    }
    if (response.ok) {
      backendPersistenceAvailable = true;
    }
  } catch {
    // Best-effort sync only.
  } finally {
    inFlightRemoteRequests.delete(controller);
  }
}

function queueRemoteSync(
  documentId: string,
  partial: {
    tabs?: PersistedEnvelope<TabsState>;
    review?: PersistedEnvelope<unknown[]>;
  },
  scopeToken?: StorageScopeToken
): void {
  const capturedScope = scopeToken ?? captureStorageScope();
  if (!capturedScope || !isStorageScopeCurrent(capturedScope)) return;
  if (capturedScope.userScope === "anonymous") return;
  if (backendPersistenceAvailable === false) return;

  const pendingKey = getPendingWorkKey(capturedScope, documentId);
  const current = pendingRemoteSync.get(pendingKey);
  pendingRemoteSync.set(pendingKey, {
    documentId,
    scopeToken: capturedScope,
    payload: { ...current?.payload, ...partial },
  });

  const existingTimer = pendingRemoteTimers.get(pendingKey);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }

  const timer = setTimeout(() => {
    void flushRemoteSync(pendingKey);
  }, REMOTE_SYNC_DEBOUNCE_MS);

  pendingRemoteTimers.set(pendingKey, timer);
}

function hasLiveLoadingTab(state: TabsState): boolean {
  return state.tabs.some((tab) => Boolean(tab.isLoading));
}

function writeTabsEnvelopeForDocumentId(
  documentId: string,
  state: TabsState,
  scopeToken: StorageScopeToken
): void {
  if (!isStorageScopeCurrent(scopeToken)) return;
  const toSave = prepareTabsStateForPersistence(state);
  const key = getTabsStorageKeyForDocument(documentId, scopeToken.userScope);
  const envelope = createEnvelope<TabsState>(
    toSave,
    TABS_RETENTION_MS,
    documentId,
    scopeToken.userScope
  );
  volatileTabsEnvelopes.set(
    getScopedSuffix(documentId, scopeToken.userScope),
    envelope
  );
  writeEnvelope(key, envelope);
  queueRemoteSync(documentId, { tabs: envelope }, scopeToken);
}

function flushPendingLocalTabsSave(
  documentId: string,
  scopeToken?: StorageScopeToken
): void {
  const capturedScope = scopeToken ?? captureStorageScope();
  if (!capturedScope) return;
  const pendingKey = getPendingWorkKey(capturedScope, documentId);
  const timer = pendingLocalTabsTimers.get(pendingKey);
  if (timer) {
    clearTimeout(timer);
    pendingLocalTabsTimers.delete(pendingKey);
  }
  const pending = pendingLocalTabsState.get(pendingKey);
  if (!pending) return;
  pendingLocalTabsState.delete(pendingKey);
  if (!isStorageScopeCurrent(pending.scopeToken)) return;
  writeTabsEnvelopeForDocumentId(
    pending.documentId,
    pending.state,
    pending.scopeToken
  );
}

function getTabsEnvelopeForScope(
  documentId: string,
  userScope: string
): PersistedEnvelope<TabsState> | null {
  const persisted = readEnvelope<TabsState>(
    getTabsStorageKeyForDocument(documentId, userScope),
    documentId,
    userScope,
    (raw) => parseTabsState(raw, documentId)
  );
  const volatile =
    volatileTabsEnvelopes.get(getScopedSuffix(documentId, userScope)) ?? null;
  const volatileIsUsable =
    volatile !== null &&
    volatile.userScope === userScope &&
    volatile.documentId === documentId &&
    volatile.expiresAt > Date.now();
  if (!volatileIsUsable) {
    if (volatile) {
      volatileTabsEnvelopes.delete(getScopedSuffix(documentId, userScope));
    }
    return persisted;
  }
  if (!persisted || volatile.storedAt >= persisted.storedAt) {
    return volatile;
  }
  return persisted;
}

function getReviewEnvelopeForScope(
  documentId: string,
  userScope: string
): PersistedEnvelope<unknown[]> | null {
  return readEnvelope<unknown[]>(
    getReviewStorageKeyForDocument(documentId, userScope),
    documentId,
    userScope,
    parseReviewResults
  );
}

/**
 * Create a new empty session for a slide reference
 */
function createSession(slideRef: SlideRef): ChatSession {
  const slideNumber = slideRef.slideNumber ?? slideRef.index + 1;
  return {
    id: createChatSessionId(),
    name: `Slide ${slideNumber}`,
    messages: [],
    slideId: slideRef.id,
    slideIndex: slideRef.index,
    slideNumber,
    selectedUserFileIds: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/**
 * Create a new tab for a slide reference
 */
export function createTab(slideRef: SlideRef): Tab {
  return {
    id: generateId(),
    session: createSession(slideRef),
    isLoading: false,
    loadingStatus: "",
    streamingThinking: "",
    thoughtProcessCollapseSignal: undefined,
    visionFeedback: "",
    refinementStep: 0,
  };
}

/**
 * Create initial tabs state
 */
/**
 * @param documentId Presentation id this state belongs to. Optional for
 * test fixtures; production callers should pass the real id so the
 * persist effect can route by `state.documentId`.
 */
export function createInitialTabsState(documentId?: string): TabsState {
  const defaultTab = createTab({ id: "", index: 0 });
  return {
    documentId,
    tabs: [defaultTab],
    activeTabId: defaultTab.id,
  };
}

/**
 * Demote stale loading flags on a persisted TabsState.
 *
 * Called by `useTabs` after loading (and after document-change reloads) with
 * a predicate that queries the operationCoordinator's in-flight registry —
 * we accept the predicate rather than importing the coordinator directly to
 * avoid a circular dependency (storageService -> coordinator -> presentation
 * identity -> storageService).
 *
 * For any tab where `isLoading=true` AND the predicate says no flow is
 * actively targeting (documentId, tab.id), we:
 * - clear `isLoading`,
 * - set `loadingStatus="Interrupted"`,
 * - preserve `streamingThinking` so the last visible reasoning stays put.
 *
 * Idempotent: tabs that are still genuinely in flight are returned untouched.
 */
export function reconcileLoadingWithRegistry(
  documentId: string,
  state: TabsState,
  isFlowLive: (documentId: string, tabId: string) => boolean
): TabsState {
  let didMutate = false;
  const reconciledTabs = state.tabs.map((tab) => {
    if (!tab.isLoading) return tab;
    if (isFlowLive(documentId, tab.id)) return tab;
    didMutate = true;
    return {
      ...tab,
      isLoading: false,
      loadingStatus: INTERRUPTED_LOADING_LABEL,
      // streamingThinking preserved verbatim (do NOT clear).
      visionFeedback: "",
      refinementStep: 0,
    };
  });
  if (!didMutate) return state;
  return { ...state, tabs: reconciledTabs };
}

function clampPersistedThinking(value: string): string {
  if (!value) return value;
  if (value.length <= MAX_PERSISTED_THINKING_BYTES) return value;
  // Trim from the start; the tail (most recent reasoning) is what users see
  // when reopening an interrupted tab.
  return value.slice(value.length - MAX_PERSISTED_THINKING_BYTES);
}

function prepareTabsStateForPersistence(state: TabsState): TabsState {
  return {
    ...state,
    tabs: state.tabs.map((tab) => ({
      ...tab,
      streamingThinking: clampPersistedThinking(tab.streamingThinking || ""),
    })),
  };
}

function messageCitationPayloadSignature(message: ChatMessage): string {
  return JSON.stringify({
    thinking: message.thinking ?? "",
    slideNumber: message.slideNumber ?? null,
    citations: message.citations ?? null,
    citationsById: message.citationsById ?? null,
    citedValuesByUuid: message.citedValuesByUuid ?? null,
    modificationCitations: message.modifications?.citations ?? null,
  });
}

function messageCitationPayloadRank(message: ChatMessage): number {
  const citationsCount = Array.isArray(message.citations) ? message.citations.length : 0;
  const citationsByIdCount = Object.keys(message.citationsById ?? {}).length;
  const citedValuesCount = Object.keys(message.citedValuesByUuid ?? {}).length;
  const modificationCitations = message.modifications?.citations;
  const modificationCitationCount = Array.isArray(modificationCitations)
    ? modificationCitations.length
    : 0;

  return citationsCount + citationsByIdCount + citedValuesCount + modificationCitationCount;
}

function messageNonCitationPayloadRank(message: ChatMessage): number {
  return ((message.thinking ?? "").trim() ? 1 : 0) + (message.slideNumber !== undefined ? 1 : 0);
}

function storedMessageHasFresherCitationPayload(
  storedMessage: ChatMessage,
  candidateMessage: ChatMessage
): boolean {
  if (
    messageCitationPayloadSignature(storedMessage) ===
    messageCitationPayloadSignature(candidateMessage)
  ) {
    return false;
  }

  // Direction matters. This guard protects an eager-saved citation-rich
  // message from a stale React snapshot, but must not block the inverse:
  // React adding the freshly completed citation payload to older storage.
  const storedCitationRank = messageCitationPayloadRank(storedMessage);
  const candidateCitationRank = messageCitationPayloadRank(candidateMessage);
  if (storedCitationRank !== candidateCitationRank) {
    return storedCitationRank > candidateCitationRank;
  }

  return (
    messageNonCitationPayloadRank(storedMessage) >
    messageNonCitationPayloadRank(candidateMessage)
  );
}

/**
 * Save tabs state to localStorage (document-specific).
 *
 * STORAGE_VERSION 3 persists in-flight visual state (`isLoading`,
 * `loadingStatus`, `streamingThinking`, `visionFeedback`, `refinementStep`)
 * verbatim so a presentation switch — and even a reload — preserves the
 * user-visible loading affordance. The companion
 * `reconcileLoadingWithRegistry` helper demotes any tab that's no longer in
 * flight back to a non-loading "Interrupted" state on next load.
 */
export function saveTabsState(state: TabsState): void {
  try {
    maybePruneExpiredEntries();
    const documentId = getDocumentId();
    saveTabsStateForDocumentId(documentId, state);
  } catch (e) {
    console.error("Failed to save tabs state:", e);
  }
}

/**
 * Load tabs state from localStorage (document-specific)
 */
export function loadTabsState(
  accountScopeKey?: string | null
): TabsState | null {
  try {
    maybePruneExpiredEntries();
    const scopeToken = captureStorageScope(accountScopeKey);
    if (!scopeToken) return null;
    const documentId = getDocumentId();
    flushPendingLocalTabsSave(documentId, scopeToken);
    const envelope = getTabsEnvelopeForScope(
      documentId,
      scopeToken.userScope
    );
    if (envelope) {
      console.log(`[Storage] Loaded ${envelope.payload.tabs.length} tabs for document`);
      return envelope.payload;
    }
    console.log(`[Storage] No saved tabs for document, starting fresh`);
  } catch (e) {
    console.error("Failed to load tabs state:", e);
  }
  return null;
}

/**
 * Load tabs state for an arbitrary documentId (NOT necessarily the current
 * active one). Used by the operation coordinator + chat flow so a message
 * produced by a flow that originated in file A can be persisted to file A
 * even if the user has since switched to file B.
 */
export function loadTabsStateForDocumentId(
  documentId: string,
  accountScopeKey?: string | null
): TabsState | null {
  try {
    const scopeToken = captureStorageScope(accountScopeKey);
    if (!scopeToken) return null;
    flushPendingLocalTabsSave(documentId, scopeToken);
    const envelope = getTabsEnvelopeForScope(
      documentId,
      scopeToken.userScope
    );
    if (envelope) {
      return envelope.payload;
    }
  } catch (e) {
    console.error(`Failed to load tabs state for documentId=${documentId}:`, e);
  }
  return null;
}

/**
 * Returns true when the persisted envelope for `documentId` already holds a
 * tabs state that DIFFERS from `candidate` in a way that suggests storage
 * is the more recent writer — i.e. a concurrent eager writer (typically
 * `useQueryHandler`'s `applyToSourceTabs`) has landed updates that React
 * hasn't yet handed back to `tabsStateRef`.
 *
 * Used by the `useTabs` document-change handler before it pre-emptively
 * saves the outgoing presentation's state under the OLD documentId, and by
 * `hydrateTabsFromBackend` before swapping React state on a hydrate result.
 * Without this gate those paths clobber an in-flight chat write:
 *
 *   1. mid-thinking, chat's `applyToSourceTabs` eager-saves the new user/
 *      assistant message OR a loading-affordance update (streamingThinking,
 *      loadingStatus, visionFeedback, refinementStep, isLoading) into
 *      docA's envelope (synchronously, inside the setTabsState updater —
 *      fires even if React batches/discards the update).
 *   2. React hasn't committed yet so the `useTabs` persist effect hasn't
 *      run, and `tabsStateRef.current` is still the PRE-update snapshot.
 *   3. The 2s document-change poll fires, sees the file switch, reads the
 *      stale `tabsStateRef.current`, and writes it back under docA —
 *      overwriting the freshly-saved update.
 *
 * Coverage (per tab):
 *   - Structural: more tabs OR a tab present in storage and missing here.
 *   - Message content: `messages.length` or `session.updatedAt` newer in
 *     storage, plus assistant-turn citation/thinking payloads that are richer
 *     in storage than in the candidate.
 *   - Loading-affordance fields (any difference counts as "potentially
 *     fresher" because these are updated by `applyToSourceTabs` WITHOUT
 *     bumping `session.updatedAt`): `loadingStatus`, `isLoading`,
 *     `streamingThinking`, `visionFeedback`, `refinementStep`.
 *
 * We deliberately do NOT compare envelope `storedAt` (clock skew + the
 * eager save also bumps it for trivial flag flips). The trade-off: when
 * storage and candidate disagree on, say, `isLoading=true` vs `false`, we
 * conservatively treat storage as fresher and skip the outgoing/hydrate
 * save. That's the safe direction for the multi-file race — the next
 * persist effect tick (running on whichever state actually committed) will
 * re-establish the correct value.
 */
export function isStorageTabsStateFresher(
  documentId: string,
  candidate: TabsState,
  accountScopeKey?: string | null
): boolean {
  let stored: TabsState | null = null;
  try {
    const scopeToken = captureStorageScope(accountScopeKey);
    if (!scopeToken) return false;
    stored =
      getTabsEnvelopeForScope(documentId, scopeToken.userScope)?.payload ??
      null;
  } catch {
    return false;
  }
  if (!stored) return false;
  if (stored.tabs.length > candidate.tabs.length) return true;
  const candidateById = new Map(candidate.tabs.map((tab) => [tab.id, tab]));
  for (const storedTab of stored.tabs) {
    const candidateTab = candidateById.get(storedTab.id);
    if (!candidateTab) return true;
    const storedUpdatedAt = storedTab.session.updatedAt ?? 0;
    const candidateUpdatedAt = candidateTab.session.updatedAt ?? 0;
    if (
      (storedTab.session.messages?.length ?? 0) >
      (candidateTab.session.messages?.length ?? 0)
    ) {
      return true;
    }
    if (storedUpdatedAt > candidateUpdatedAt) {
      return true;
    }
    const candidateTabIsNewer = candidateUpdatedAt > storedUpdatedAt;
    // Message-level divergence guard. Even when counts and updatedAt are
    // equal, storage may still contain a newer eager-save snapshot (for
    // example: assistant turn mirrored with modifications, or a patched
    // content field) while React still holds a stale in-memory snapshot.
    // Shell mismatches mean "do not overwrite storage". Citation payload
    // mismatches are directional below: richer storage wins, richer candidate
    // must still be allowed to save.
    const storedMessages = storedTab.session.messages ?? [];
    const candidateMessages = candidateTab.session.messages ?? [];
    if (!candidateTabIsNewer && storedMessages.length === candidateMessages.length) {
      for (let i = 0; i < storedMessages.length; i++) {
        const storedMessage = storedMessages[i];
        const candidateMessage = candidateMessages[i];
        if (!candidateMessage) {
          return true;
        }
        if (
          storedMessage.id !== candidateMessage.id ||
          storedMessage.role !== candidateMessage.role ||
          storedMessage.content !== candidateMessage.content ||
          (storedMessage.slideIndex ?? -1) !== (candidateMessage.slideIndex ?? -1)
        ) {
          return true;
        }
        const storedHasModifications = Boolean(storedMessage.modifications);
        const candidateHasModifications = Boolean(candidateMessage.modifications);
        if (storedHasModifications !== candidateHasModifications) {
          return true;
        }
        if (storedMessageHasFresherCitationPayload(storedMessage, candidateMessage)) {
          return true;
        }
      }
    }
    if (candidateTabIsNewer) {
      continue;
    }
    // Loading-affordance comparisons. Any disagreement => skip the save:
    // `applyToSourceTabs` writes these fields without bumping
    // `session.updatedAt`, so they're the canonical signal that an eager
    // save has landed between the last persist and the doc-change /
    // hydrate read of `tabsStateRef.current`.
    if ((storedTab.loadingStatus ?? "") !== (candidateTab.loadingStatus ?? "")) {
      return true;
    }
    if (Boolean(storedTab.isLoading) !== Boolean(candidateTab.isLoading)) {
      return true;
    }
    if (
      (storedTab.streamingThinking ?? "") !==
      (candidateTab.streamingThinking ?? "")
    ) {
      return true;
    }
    if ((storedTab.visionFeedback ?? "") !== (candidateTab.visionFeedback ?? "")) {
      return true;
    }
    if ((storedTab.refinementStep ?? 0) !== (candidateTab.refinementStep ?? 0)) {
      return true;
    }
  }
  return false;
}

/**
 * Persist tabs state to an arbitrary documentId envelope. Mirror of
 * `saveTabsState` that does NOT default to the current active presentation.
 * Loading flags are intentionally KEPT — cross-presentation loading state
 * preservation relies on them surviving a save.
 *
 * Safety net: when `state.documentId` disagrees with the caller-supplied
 * `documentId`, route to `state.documentId` and log. Belt-and-braces
 * backstop for the multi-switch race the cb-side guards already cover.
 */
export function saveTabsStateForDocumentId(
  documentId: string,
  state: TabsState,
  accountScopeKey?: string | null
): void {
  saveTabsStateForDocumentIdWithOptions(documentId, state, {
    immediate: true,
    accountScopeKey,
  });
}

export function saveTabsStateForDocumentIdWithOptions(
  documentId: string,
  state: TabsState,
  options?: {
    immediate?: boolean;
    accountScopeKey?: string | null;
  }
): void {
  let targetDocId = documentId;
  try {
    const scopeToken = captureStorageScope(options?.accountScopeKey);
    if (!scopeToken) return;
    if (state.documentId && state.documentId !== documentId) {
      console.error(
        `[Storage] Refusing to save tabs state under wrong documentId. ` +
          `state.documentId=${state.documentId} caller=${documentId}. ` +
          `Routing to state.documentId instead.`
      );
      targetDocId = state.documentId;
    }
    const shouldWriteImmediately = options?.immediate ?? !hasLiveLoadingTab(state);
    if (shouldWriteImmediately) {
      flushPendingLocalTabsSave(targetDocId, scopeToken);
      writeTabsEnvelopeForDocumentId(targetDocId, state, scopeToken);
      return;
    }
    const pendingKey = getPendingWorkKey(scopeToken, targetDocId);
    pendingLocalTabsState.set(pendingKey, {
      documentId: targetDocId,
      scopeToken,
      state,
    });
    const existingTimer = pendingLocalTabsTimers.get(pendingKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    const timer = setTimeout(() => {
      flushPendingLocalTabsSave(targetDocId, scopeToken);
    }, LOCAL_TABS_SAVE_DEBOUNCE_MS);
    pendingLocalTabsTimers.set(pendingKey, timer);
  } catch (e) {
    console.error(`Failed to save tabs state for documentId=${targetDocId}:`, e);
  }
}

/**
 * Best-effort backend hydration. `documentId` is explicit so the caller
 * can scope the fetch to the file active when hydration was requested,
 * not whatever the FactSet poll flips to mid-await.
 */
export async function loadTabsStateFromBackend(
  documentId: string,
  accountScopeKey?: string | null
): Promise<TabsState | null> {
  const scopeToken = captureStorageScope(accountScopeKey);
  if (!scopeToken) return null;
  const remoteState = await fetchRemoteDocumentState(documentId, scopeToken);
  if (!isStorageScopeCurrent(scopeToken)) return null;
  if (!remoteState || !isEnvelope(remoteState.tabs)) {
    return null;
  }

  if (
    remoteState.tabs.userScope !== scopeToken.userScope ||
    remoteState.tabs.documentId !== documentId ||
    remoteState.tabs.expiresAt <= Date.now()
  ) {
    return null;
  }

  const parsedRemote = parseTabsState(remoteState.tabs.payload, documentId);
  if (!parsedRemote) {
    return null;
  }

  const local = getTabsEnvelopeForScope(documentId, scopeToken.userScope);
  if (local && local.storedAt >= remoteState.tabs.storedAt) {
    return null;
  }

  const normalizedRemote = {
    ...remoteState.tabs,
    payload: parsedRemote,
  } as PersistedEnvelope<TabsState>;

  if (!isStorageScopeCurrent(scopeToken)) return null;
  writeEnvelope(
    getTabsStorageKeyForDocument(documentId, scopeToken.userScope),
    normalizedRemote
  );
  return normalizedRemote.payload;
}

/**
 * Save review results with user/document scoping and retention metadata.
 */
export function saveReviewResults(
  results: unknown[],
  accountScopeKey: string | null
): void {
  try {
    maybePruneExpiredEntries();
    const scopeToken = captureStorageScope(accountScopeKey);
    if (!scopeToken) return;
    const documentId = getDocumentId();
    const envelope = createEnvelope(
      results,
      REVIEW_RETENTION_MS,
      documentId,
      scopeToken.userScope
    );
    writeEnvelope(
      getReviewStorageKeyForDocument(documentId, scopeToken.userScope),
      envelope
    );
    queueRemoteSync(documentId, { review: envelope }, scopeToken);
  } catch (error) {
    console.error("Failed to save review results:", error);
  }
}

/**
 * Load review results for current user/document from local storage.
 *
 * The optional shape temporarily preserves source compatibility for callers
 * that still need accountScopeKey wiring. Missing scope fails closed instead
 * of reading whichever global account happens to be current.
 */
export function loadReviewResults(
  accountScopeKey?: string | null
): unknown[] {
  try {
    if (accountScopeKey === undefined) return [];
    maybePruneExpiredEntries();
    const scopeToken = captureStorageScope(accountScopeKey);
    if (!scopeToken) return [];
    const documentId = getDocumentId();
    const envelope = getReviewEnvelopeForScope(
      documentId,
      scopeToken.userScope
    );
    return envelope?.payload || [];
  } catch {
    return [];
  }
}

/**
 * Best-effort backend hydration for review results.
 * Returns newer results when available.
 */
export async function loadReviewResultsFromBackend(
  accountScopeKey: string | null
): Promise<unknown[] | null> {
  const scopeToken = captureStorageScope(accountScopeKey);
  if (!scopeToken) return null;
  const documentId = getDocumentId();
  const remoteState = await fetchRemoteDocumentState(documentId, scopeToken);
  if (!isStorageScopeCurrent(scopeToken)) return null;
  if (!remoteState || !isEnvelope(remoteState.review)) {
    return null;
  }

  if (
    remoteState.review.userScope !== scopeToken.userScope ||
    remoteState.review.documentId !== documentId ||
    remoteState.review.expiresAt <= Date.now()
  ) {
    return null;
  }

  const parsedRemote = parseReviewResults(remoteState.review.payload);
  if (!parsedRemote) {
    return null;
  }

  const local = getReviewEnvelopeForScope(documentId, scopeToken.userScope);
  if (local && local.storedAt >= remoteState.review.storedAt) {
    return null;
  }

  const normalizedRemote = {
    ...remoteState.review,
    payload: parsedRemote,
  } as PersistedEnvelope<unknown[]>;

  if (!isStorageScopeCurrent(scopeToken)) return null;
  writeEnvelope(
    getReviewStorageKeyForDocument(documentId, scopeToken.userScope),
    normalizedRemote
  );
  return normalizedRemote.payload;
}

/**
 * Add a new tab for a slide reference, or switch to an existing one.
 */
export function addTab(state: TabsState, slideRef: SlideRef): TabsState | null {
  const existingTab = state.tabs.find((t) => {
    if (slideRef.id) {
      if (t.session.slideId) {
        return t.session.slideId === slideRef.id;
      }
      return t.session.slideIndex === slideRef.index && t.session.messages.length === 0;
    }
    return t.session.slideIndex === slideRef.index;
  });
  if (existingTab) {
    const migratedTabs = state.tabs.map((tab) => {
      if (tab.id !== existingTab.id) {
        return tab;
      }
      const nextSlideId = tab.session.slideId || slideRef.id;
      const nextSlideIndex = slideRef.index;
      const nextSlideNumber =
        slideRef.slideNumber ??
        (tab.session.slideIndex === slideRef.index ? tab.session.slideNumber : undefined) ??
        slideRef.index + 1;
      const nextSlideName = `Slide ${nextSlideNumber}`;
      const shouldUpdateMetadata =
        tab.session.slideId !== nextSlideId ||
        tab.session.slideIndex !== nextSlideIndex ||
        tab.session.slideNumber !== nextSlideNumber ||
        tab.session.name !== nextSlideName;
      if (!shouldUpdateMetadata) {
        return tab;
      }
      return {
        ...tab,
        session: {
          ...tab.session,
          slideId: nextSlideId,
          slideIndex: nextSlideIndex,
          slideNumber: nextSlideNumber,
          name: nextSlideName,
          updatedAt: Date.now(),
        },
      };
    });

    return {
      ...state,
      tabs: migratedTabs,
      activeTabId: existingTab.id,
    };
  }

  const newTab = createTab(slideRef);
  return {
    ...state,
    tabs: [...state.tabs, newTab],
    activeTabId: newTab.id,
  };
}

/**
 * Close a tab
 */
export function closeTab(state: TabsState, tabId: string): TabsState {
  const newTabs = state.tabs.filter((t) => t.id !== tabId);

  // Preserve documentId so the default tab persists under the same envelope.
  if (newTabs.length === 0) {
    return createInitialTabsState(state.documentId);
  }

  // If closing active tab, switch to another
  let newActiveId = state.activeTabId;
  if (state.activeTabId === tabId) {
    const closedIndex = state.tabs.findIndex((t) => t.id === tabId);
    const newIndex = Math.min(closedIndex, newTabs.length - 1);
    newActiveId = newTabs[newIndex].id;
  }

  return {
    ...state,
    tabs: newTabs,
    activeTabId: newActiveId,
  };
}

/**
 * Update a specific tab
 */
export function updateTab(state: TabsState, tabId: string, updates: Partial<Tab>): TabsState {
  return {
    ...state,
    tabs: state.tabs.map((tab) => (tab.id === tabId ? { ...tab, ...updates } : tab)),
  };
}

/**
 * Get the active tab
 */
export function getActiveTab(state: TabsState): Tab | undefined {
  return state.tabs.find((t) => t.id === state.activeTabId);
}

/**
 * Clear chat history for a tab
 */
export function clearTabHistory(state: TabsState, tabId: string): TabsState {
  return {
    ...state,
    tabs: state.tabs.map((tab) =>
      tab.id === tabId
        ? {
            ...tab,
            session: {
              ...tab.session,
              messages: [],
              updatedAt: Date.now(),
            },
            streamingThinking: "",
            visionFeedback: "",
          }
        : tab
    ),
  };
}

export function saveReferenceDeck(content: PresentationContent): void {
  withLocalStorage(() => localStorage.setItem(REF_DECK_KEY, JSON.stringify(content)), undefined);
}

export function loadReferenceDeck(): PresentationContent | null {
  return withLocalStorage(() => {
    const raw = localStorage.getItem(REF_DECK_KEY);
    return raw ? (JSON.parse(raw) as PresentationContent) : null;
  }, null);
}

export function clearReferenceDeck(): void {
  withLocalStorage(() => localStorage.removeItem(REF_DECK_KEY), undefined);
}

const REF_DECK_PENDING_KEY = "finster_reference_deck_pending";

export function savePendingReferencePptx(base64: string): void {
  withLocalStorage(() => localStorage.setItem(REF_DECK_PENDING_KEY, base64), undefined);
}

export function loadPendingReferencePptx(): string | null {
  return withLocalStorage(() => localStorage.getItem(REF_DECK_PENDING_KEY), null);
}

export function clearPendingReferencePptx(): void {
  withLocalStorage(() => localStorage.removeItem(REF_DECK_PENDING_KEY), undefined);
}
